/**
 * Télémétrie d'erreurs BASIQUE (spec 15 §8 — Phase 5 « télémétrie d'erreurs
 * basique ») : primitive de site réutilisable. `reportError` journalise une
 * erreur de façon structurée et, SI `VITE_TELEMETRY_URL` est configurée, envoie
 * un beacon JSON minimal (jamais de PII — ni compte, ni email). Par défaut,
 * aucune destination n'est configurée → aucun envoi réseau (no-op) → prod
 * intacte tant qu'un opérateur n'active pas explicitement une destination.
 *
 * Ne lève JAMAIS : une télémétrie qui casse ne doit pas aggraver l'incident
 * qu'elle signale. Premier consommateur : l'aire Panda Panic (frontière d'erreur
 * + garde de boucle de rendu).
 */

export interface ErrorContext {
  /** Étiquette de l'endroit qui a échoué (ex. `panda-panic:online-loop`). */
  readonly context?: string;
  /** Métadonnées non sensibles supplémentaires (jamais de PII). */
  readonly [key: string]: unknown;
}

function telemetryUrl(): string | undefined {
  const url = import.meta.env.VITE_TELEMETRY_URL;
  return typeof url === 'string' && url.length > 0 ? url : undefined;
}

/**
 * Signale une erreur. Toujours journalisée en console (visible en dev et dans
 * les rapports de bug) ; envoyée en beacon uniquement si une destination est
 * configurée et que `sendBeacon` existe (absent en jsdom/tests → no-op).
 */
export function reportError(error: unknown, context: ErrorContext = {}): void {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    console.error(
      '[telemetry] erreur signalée',
      { ...context, name: err.name, message: err.message },
      err,
    );

    const url = telemetryUrl();
    if (!url) return; // pas de destination → no-op réseau (prod intacte)
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;

    const payload = {
      ...context,
      name: err.name,
      message: err.message,
      // Trace tronquée : utile au tri des incidents, bornée pour rester légère.
      stack: typeof err.stack === 'string' ? err.stack.slice(0, 2000) : undefined,
      path: typeof location !== 'undefined' ? location.pathname : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };
    navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  } catch {
    // Une erreur DANS la télémétrie ne doit jamais remonter.
  }
}
